import React, { useState } from 'react';
import {
  Box, Card, CardContent, CardActions, Typography, Button, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, Stack, Chip, Grid, Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import HomeIcon from '@mui/icons-material/Home';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';

const EMPTY_ADDR = { address1: '', name: '', pincode: '', state: '', type: 'Home', phone_number: '' };

const AddressManager = ({ addresses = [], onAdd, onUpdate, onDelete, onSetDefault, loading, readOnly = false }) => {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = add mode, id = edit mode
  const [form, setForm] = useState(EMPTY_ADDR);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const openAdd = () => { setEditing(null); setForm(EMPTY_ADDR); setOpen(true); };
  const openEdit = (addr) => { setEditing(addr.id); setForm({ ...addr }); setOpen(true); };
  const handleClose = () => setOpen(false);

  const handleSave = () => {
    if (editing !== null) onUpdate(editing, form);
    else onAdd(form);
    handleClose();
  };

  const typeIcon = (type) => type === 'Office' ? <BusinessIcon fontSize="small" /> : <HomeIcon fontSize="small" />;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="subtitle1" fontWeight={600}>Saved Addresses</Typography>
        {!readOnly && (
          <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={openAdd}>
            Add Address
          </Button>
        )}
      </Stack>

      {addresses.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
          <LocationOnIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
          <Typography variant="body2">No addresses saved yet</Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {addresses.map(addr => (
          <Card key={addr.id} variant="outlined" sx={{ borderRadius: 2, position: 'relative' }}>
            {addr.default ? (
              <Chip label="Default" size="small" color="primary" sx={{ position: 'absolute', top: 10, right: 10 }} />
            ) : null}
            <CardContent sx={{ pb: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                {typeIcon(addr.type)}
                <Typography variant="subtitle2" fontWeight={600}>{addr.type}</Typography>
                {addr.name && <Typography variant="body2" color="text.secondary">· {addr.name}</Typography>}
              </Stack>
              <Typography variant="body2" color="text.secondary">{addr.address1}</Typography>
              <Typography variant="body2" color="text.secondary">{addr.state} {addr.pincode}</Typography>
              {addr.phone_number && <Typography variant="caption" color="text.secondary">{addr.phone_number}</Typography>}
            </CardContent>
            <Divider />
            <CardActions sx={{ px: 2, py: 0.5 }}>
              {!readOnly && !addr.default && (
                <Button size="small" onClick={() => onSetDefault(addr.id)}>Set Default</Button>
              )}
              {!readOnly && (
                <IconButton size="small" onClick={() => openEdit(addr)}><EditIcon fontSize="small" /></IconButton>
              )}
              {!readOnly && (
                <IconButton size="small" color="error" onClick={() => setDeleteConfirm(addr.id)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </CardActions>
          </Card>
        ))}
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editing !== null ? 'Edit Address' : 'Add Address'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="Label / Name"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                fullWidth
              />
              <TextField
                select
                label="Type"
                value={form.type}
                onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                fullWidth
              >
                {['Home', 'Office', 'Event Venue', 'Other'].map(t => (
                  <MenuItem key={t} value={t}>{t}</MenuItem>
                ))}
              </TextField>
            </Box>
            <TextField
              label="Street Address"
              value={form.address1}
              onChange={e => setForm(f => ({ ...f, address1: e.target.value }))}
              fullWidth
              multiline
              rows={2}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="State / Region"
                value={form.state}
                onChange={e => setForm(f => ({ ...f, state: e.target.value }))}
                fullWidth
              />
              <TextField
                label="Postcode / PIN"
                value={form.pincode}
                onChange={e => setForm(f => ({ ...f, pincode: e.target.value }))}
                fullWidth
              />
            </Box>
            <TextField
              label="Contact Number"
              value={form.phone_number}
              onChange={e => setForm(f => ({ ...f, phone_number: e.target.value }))}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={deleteConfirm !== null} onClose={() => setDeleteConfirm(null)} maxWidth="xs">
        <DialogTitle>Delete Address?</DialogTitle>
        <DialogContent>
          <Typography>This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => { onDelete(deleteConfirm); setDeleteConfirm(null); }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AddressManager;
